import { NgClass } from "@angular/common";
import { Component, Input, ViewChild, ViewContainerRef } from "@angular/core";
import { PonderTasksClient } from "@apiserver-rest/ponder-tasks-client";
import { CreateTaskScheduleRequest, CreateTaskScheduleResponse, ListTaskSchedulesResponse, TaskDescription, TaskResponse, TaskSchedule2, UpdateTaskScheduleRequest } from "@apiserver-rest/ponder-tasks-client-base";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { ConfirmationModalComponent } from "@ponder-shared/modals/confirmation-modal.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { take } from "rxjs";

@Component({
    selector: "task-schedule-table",
    imports: [NgClass, LoadingComponent],
    templateUrl: "./task-schedule-table.component.html"
})
export class TaskScheduleTableComponent {
    @Input({ required: true }) public task!: TaskDescription;
    
    @ViewChild("confirmationModalTemplate", { read: ViewContainerRef }) private confirmationModalVcr!: ViewContainerRef;

    public isLoading: boolean = false;
    public loadingSuccess: boolean = false;
    public taskSchedules?: TaskSchedule2[];

    public readonly playSvgIcon: string = SvgIconMap.get(SvgIcon.Play)!;
    public readonly pauseSvgIcon: string = SvgIconMap.get(SvgIcon.Pause)!;
    public readonly deleteSvgIcon: string = SvgIconMap.get(SvgIcon.Delete)!;

    constructor(private ponderTasksClient: PonderTasksClient) {

    }

    public ngOnOnit(): void {
        this.reload();
        this.fetchData();
    }

    public ngOnChanges(): void {
        this.reload();
        this.fetchData();
    }

    private reload(): void {
        this.isLoading = false;
        this.loadingSuccess = false;
        this.taskSchedules = undefined;
        this.isAddingSchedule = false;
        this.addingScheduleError = false;
        this.isRunningAction = false;
    }

    private fetchData(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.loadingSuccess = false;
        this.taskSchedules = undefined;

        this.ponderTasksClient
            .tasksListScheduleTask(this.task.taskName)
            .pipe(take(1))
            .subscribe({
                next: (response: ListTaskSchedulesResponse) => {
                    if (response.success) {
                        this.taskSchedules = response.schedules;
                        this.loadingSuccess = true;
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    public isRunningAction: boolean = false;

    public onResumeClicked(taskSchedule: TaskSchedule2): void {
        if (this.isRunningAction) {
            return;
        }

        this.isRunningAction = true;

        this.ponderTasksClient
            .tasksScheduleTaskPatch(new UpdateTaskScheduleRequest({
                scheduleId: taskSchedule.id,
                enabled: true
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: TaskResponse) => {
                    if (response.success) {
                        taskSchedule.enabled = true;
                    }
                }
            })
            .add(() => { this.isRunningAction = false; });
    }

    // -------------------------------------------------------------------------------------
    // Add Schedule
    // -------------------------------------------------------------------------------------

    public isAddingSchedule: boolean = false;
    public addingScheduleError: boolean = false;

    public onAddSchedule(definitionName: string, minute: string, hour: string, dayOfMonth: string, month: string, dayOfWeek: string): void {
        if (this.isAddingSchedule) {
            return;
        }

        this.isAddingSchedule = true;
        this.addingScheduleError = false;

        this.ponderTasksClient
            .tasksScheduleTaskPost(new CreateTaskScheduleRequest({
                taskName: this.task.taskName,
                taskDefinitionName: definitionName,
                cronSchedule: `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`,
                enabled: false      // start all as paused by default
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: CreateTaskScheduleResponse) => {
                    if (response.success && response.schedule) {
                        this.taskSchedules = this.taskSchedules ?? [];
                        this.taskSchedules.push(response.schedule);
                    } else {
                        this.addingScheduleError = true;
                    }
                },
                error: () => { this.addingScheduleError = true; }
            })
            .add(() => { this.isAddingSchedule = false; })
    }

    // -------------------------------------------------------------------------------------
    // Control Schedule
    // -------------------------------------------------------------------------------------

    public onPauseClicked(taskSchedule: TaskSchedule2): void {
        if (this.isRunningAction) {
            return;
        }

        this.isRunningAction = true;

        this.ponderTasksClient
            .tasksScheduleTaskPatch(new UpdateTaskScheduleRequest({
                scheduleId: taskSchedule.id,
                enabled: false
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: TaskResponse) => {
                    if (response.success) {
                        taskSchedule.enabled = false;
                    }
                }
            })
            .add(() => { this.isRunningAction = false; });
    }

    public onDeleteClicked(taskSchedule: TaskSchedule2): void {
        if (this.isRunningAction) {
            return;
        }

        this.isRunningAction = true;

        
        var confirmationModalRef = this.confirmationModalVcr.createComponent(ConfirmationModalComponent);

        confirmationModalRef.instance.title = "Delete Schedule?";
        confirmationModalRef.instance.body = `Are you sure you want to delete this schedule?`;
        confirmationModalRef.instance.centered = true;
        confirmationModalRef.instance.onAcceptCallback = () => { 
            this.ponderTasksClient
                .tasksScheduleTaskDelete(taskSchedule.id)
                .pipe(take(1))
                .subscribe({
                    next: (response: TaskResponse) => {
                        if (response.success) {
                            var index = (this.taskSchedules ? this.taskSchedules.indexOf(taskSchedule) : -1);

                            if (index >= 0) {
                                this.taskSchedules?.splice(index, 1);
                                this.taskSchedules = (this.taskSchedules?.length ? this.taskSchedules : undefined);
                            }
                        }
                    }
                })
                .add(() => { this.isRunningAction = false; });
        }
    }
}